import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Pencil, Trash2, Loader2, Check, ShoppingBag, CreditCard, Banknote, Smartphone } from "lucide-react";

interface RetailItem { id: number; name: string; category: string; price: number; stock: number; description?: string; available: boolean; }
interface RetailSale { id: number; item_id: number; item_name: string; qty: number; price: number; guest_name?: string; payment_method: string; created_at: string; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }
function fmtDT(s: string) { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }

export default function SpaRetail({ API, isSpaManager }: { API: string; isSpaManager: boolean }) {
  const [subTab, setSubTab]   = useState<"products" | "sales">("products");
  const [items, setItems]     = useState<RetailItem[]>([]);
  const [sales, setSales]     = useState<RetailSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemModal, setItemModal] = useState(false);
  const [saleModal, setSaleModal] = useState(false);
  const [editItem, setEditItem]   = useState<Partial<RetailItem> | null>(null);
  const [saleForm, setSaleForm]   = useState({ item_id: "", qty: 1, guest_name: "", payment_method: "cash", card_number: "", transaction_ref: "" });
  const [saving, setSaving]   = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/retail/items`, { headers: authH() }); setItems(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/retail/sales`, { headers: authH() }); setSales(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { if (subTab === "products") fetchItems(); else fetchSales(); }, [subTab, fetchItems, fetchSales]);

  const saveItem = async () => {
    if (!editItem?.name?.trim()) return;
    setSaving(true);
    try {
      if (editItem.id) await axios.put(`${API}/retail/items/${editItem.id}`, editItem, { headers: authH() });
      else              await axios.post(`${API}/retail/items`, editItem, { headers: authH() });
      setItemModal(false); setEditItem(null); fetchItems();
    } catch { /**/ } finally { setSaving(false); }
  };

  const delItem = async (id: number) => {
    if (!confirm("Delete this product?")) return;
    await axios.delete(`${API}/retail/items/${id}`, { headers: authH() });
    setItems(i => i.filter(x => x.id !== id));
  };

  const recordSale = async () => {
    if (!saleForm.item_id) return;
    setSaving(true);
    try {
      const item = items.find(i => i.id === Number(saleForm.item_id));
      await axios.post(`${API}/retail/sales`, {
        item_id: Number(saleForm.item_id), item_name: item?.name, qty: saleForm.qty,
        price: (item?.price || 0) * saleForm.qty, guest_name: saleForm.guest_name || undefined,
        payment_method: saleForm.payment_method,
        card_number: saleForm.payment_method === "card" ? saleForm.card_number || undefined : undefined,
        transaction_ref: saleForm.payment_method === "instapay" ? saleForm.transaction_ref || undefined : undefined,
      }, { headers: authH() });
      setSaleModal(false); setSaleForm({ item_id: "", qty: 1, guest_name: "", payment_method: "cash", card_number: "", transaction_ref: "" });
      fetchItems(); fetchSales();
    } catch { /**/ } finally { setSaving(false); }
  };

  const delSale = async (id: number) => {
    if (!confirm("Remove this sale?")) return;
    await axios.delete(`${API}/retail/sales/${id}`, { headers: authH() });
    setSales(s => s.filter(x => x.id !== id));
  };

  const selectedItem = items.find(i => i.id === Number(saleForm.item_id));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {(["products", "sales"] as const).map(t => (
            <button key={t} className={`spa-slot-staff-tab ${subTab === t ? "active" : ""}`} onClick={() => setSubTab(t)} style={{ textTransform: "capitalize" }}>{t}</button>
          ))}
        </div>
        {isSpaManager && subTab === "products" && <button className="spa-btn-primary" onClick={() => { setEditItem({ name: "", category: "Product", price: 0, stock: 0, description: "", available: true }); setItemModal(true); }}><Plus size={14} /> Add Product</button>}
        {subTab === "sales" && <button className="spa-btn-primary" onClick={() => setSaleModal(true)}><Plus size={14} /> Record Sale</button>}
      </div>

      {/* Item modal */}
      {itemModal && editItem && (
        <div className="spa-modal-overlay" onClick={() => setItemModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="spa-modal-header"><h2 className="spa-modal-title">{editItem.id ? "Edit Product" : "New Product"}</h2><button className="spa-modal-close" onClick={() => setItemModal(false)}><X size={16} /></button></div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Product Name</label><input value={editItem.name || ""} onChange={e => setEditItem(s => ({ ...s, name: e.target.value }))} placeholder="e.g. Argan Oil, Body Scrub" /></div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Category</label><input value={editItem.category || ""} onChange={e => setEditItem(s => ({ ...s, category: e.target.value }))} placeholder="Oil, Cream…" /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Price ($)</label><input type="number" min={0} value={editItem.price ?? 0} onChange={e => setEditItem(s => ({ ...s, price: Number(e.target.value) }))} /></div>
              </div>
              <div className="spa-form-row" style={{ marginTop: 12 }}>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Stock</label><input type="number" min={0} value={editItem.stock ?? 0} onChange={e => setEditItem(s => ({ ...s, stock: Number(e.target.value) }))} /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Available</label><label className="spa-checkbox-row"><input type="checkbox" checked={editItem.available ?? true} onChange={e => setEditItem(s => ({ ...s, available: e.target.checked }))} /> Active</label></div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Description</label><textarea rows={2} value={editItem.description || ""} onChange={e => setEditItem(s => ({ ...s, description: e.target.value }))} /></div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setItemModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={saveItem} disabled={saving || !editItem.name?.trim()}>{saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />}{editItem.id ? "Save" : "Add Product"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sale modal */}
      {saleModal && (
        <div className="spa-modal-overlay" onClick={() => setSaleModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="spa-modal-header"><h2 className="spa-modal-title">Record Sale</h2><button className="spa-modal-close" onClick={() => setSaleModal(false)}><X size={16} /></button></div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Product *</label>
                <select value={saleForm.item_id} onChange={e => setSaleForm(f => ({ ...f, item_id: e.target.value }))}>
                  <option value="">Select product…</option>
                  {items.filter(i => i.available).map(i => <option key={i.id} value={i.id}>{i.name} — ${i.price} (stock: {i.stock})</option>)}
                </select>
              </div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Qty</label><input type="number" min={1} value={saleForm.qty} onChange={e => setSaleForm(f => ({ ...f, qty: Number(e.target.value) }))} /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Guest Name</label><input value={saleForm.guest_name} onChange={e => setSaleForm(f => ({ ...f, guest_name: e.target.value }))} placeholder="Optional" /></div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Payment</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {([["cash", <Banknote size={13} />, "Cash"], ["card", <CreditCard size={13} />, "Card"], ["instapay", <Smartphone size={13} />, "Instapay"]] as const).map(([m, icon, label]) => (
                    <button key={m} onClick={() => setSaleForm(f => ({ ...f, payment_method: m }))} style={{ flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: saleForm.payment_method === m ? "rgba(201,168,76,0.18)" : "rgba(255,255,255,0.03)", border: `1px solid ${saleForm.payment_method === m ? "rgba(201,168,76,0.5)" : "rgba(201,168,76,0.1)"}`, color: saleForm.payment_method === m ? "#c9a84c" : "rgba(242,235,217,0.4)" }}>{icon}{label}</button>
                  ))}
                </div>
                {saleForm.payment_method === "card" && <input style={{ marginTop: 8 }} type="text" inputMode="numeric" maxLength={4} placeholder="Last 3 digits" value={saleForm.card_number} onChange={e => setSaleForm(f => ({ ...f, card_number: e.target.value.replace(/\D/g, "") }))} />}
                {saleForm.payment_method === "instapay" && <input style={{ marginTop: 8 }} type="text" placeholder="Transaction ref (optional)" value={saleForm.transaction_ref} onChange={e => setSaleForm(f => ({ ...f, transaction_ref: e.target.value }))} />}
              </div>
              {selectedItem && <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(201,168,76,0.08)", borderRadius: 8, fontSize: 13, color: "#c9a84c", fontWeight: 700 }}>Total: ${(selectedItem.price * saleForm.qty).toFixed(2)}</div>}
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setSaleModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={recordSale} disabled={saving || !saleForm.item_id}>{saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />} Record Sale</button>
            </div>
          </div>
        </div>
      )}

      {/* Products */}
      {subTab === "products" && (
        loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : items.length === 0 ? (
          <div className="spa-fully-booked"><ShoppingBag size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No products added yet</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {items.map(item => (
              <div key={item.id} style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 12, padding: "14px 16px", opacity: item.available ? 1 : 0.5 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 13 }}>{item.name}</div>
                  {isSpaManager && <div style={{ display: "flex", gap: 4 }}>
                    <button className="spa-action-btn" style={{ padding: "2px 7px" }} onClick={() => { setEditItem({ ...item }); setItemModal(true); }}><Pencil size={11} /></button>
                    <button className="spa-action-btn danger" style={{ padding: "2px 7px" }} onClick={() => delItem(item.id)}><Trash2 size={11} /></button>
                  </div>}
                </div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginBottom: 8 }}>{item.category}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#c9a84c" }}>${item.price}</span>
                  <span style={{ fontSize: 12, color: item.stock <= 3 ? "#f87171" : "rgba(242,235,217,0.4)" }}>Stock: {item.stock}</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Sales */}
      {subTab === "sales" && (
        loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : sales.length === 0 ? (
          <div className="spa-fully-booked"><ShoppingBag size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No sales recorded</div>
        ) : (
          <div className="spa-appt-table">
            <div className="spa-table-header">
              {["Product", "Qty", "Total", "Guest", "Payment", "Date", ""].map(h => <div key={h} className="spa-th">{h}</div>)}
            </div>
            {sales.map(s => (
              <div key={s.id} className="spa-table-row">
                <div className="spa-td" style={{ fontWeight: 600, color: "#F2EBD9" }}>{s.item_name}</div>
                <div className="spa-td">{s.qty}</div>
                <div className="spa-td" style={{ color: "#c9a84c", fontWeight: 700 }}>${s.price?.toFixed(2)}</div>
                <div className="spa-td">{s.guest_name || "—"}</div>
                <div className="spa-td" style={{ textTransform: "capitalize" }}>{s.payment_method}</div>
                <div className="spa-td">{fmtDT(s.created_at)}</div>
                <div className="spa-td"><button className="spa-action-btn danger" style={{ padding: "2px 8px" }} onClick={() => delSale(s.id)}><Trash2 size={11} /></button></div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
